import { useCallback, useEffect, useRef } from "react";
import { listChatActivity } from "@/lib/api";
import type { ChatActivityEntry, ChatActivityState, ChatIndicator } from "@/lib/chat";
import {
	readChatLastSeen,
	sameChatLastSeen,
	writeChatLastSeen,
	type ChatLastSeen,
} from "@/lib/chat-last-seen";
import { useChatStore } from "@/stores/useChatStore";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";
import { useLayoutStore } from "@/stores/useLayoutStore";
import { useToastStore } from "@/stores/useToastStore";

export const POLL_INTERVAL_MS = 5_000;

/** 実行中 > 操作待ち > (未読なら) エラー終了 > 未読 の優先順位で丸を 1 つ決める。 */
function indicatorFor(state: ChatActivityState, unread: boolean): ChatIndicator | null {
	if (state === "running") return "running";
	if (state === "waitingUser") return "waitingUser";
	if (!unread) return null;
	return state === "failed" ? "failed" : "unread";
}

/**
 * サーバが返した状態とブラウザの既読時刻から、チャットごとの丸と新しい既読時刻を作る。
 * 開いているチャットは常に既読。既読情報が無いブラウザでは既存チャット全件を既読として種をまく。
 */
export function resolveChatIndicators(
	entries: readonly ChatActivityEntry[],
	lastSeen: ChatLastSeen | null,
	activeChatId: string | null,
): { indicators: Record<string, ChatIndicator>; lastSeen: ChatLastSeen } {
	const indicators: Record<string, ChatIndicator> = {};
	const nextLastSeen: Record<string, string> = {};

	for (const entry of entries) {
		const seenAt =
			lastSeen === null || entry.chatId === activeChatId
				? entry.lastActivityAt
				: lastSeen[entry.chatId];
		// 削除されたチャットの既読時刻を溜め込まないよう、返ってきたチャットの分だけ引き継ぐ。
		if (seenAt !== undefined) nextLastSeen[entry.chatId] = seenAt;

		// サーバの時刻は全て同じ ISO 形式なので、文字列比較がそのまま時系列の比較になる。
		const unread = seenAt === undefined || entry.lastActivityAt > seenAt;
		const indicator = indicatorFor(entry.state, unread);
		if (indicator !== null) indicators[entry.chatId] = indicator;
	}

	return { indicators, lastSeen: nextLastSeen };
}

/**
 * チャット一覧のインジケーターをサーバと同期する。左ペインから一度だけマウントする。
 * 開いていないチャットの進行はサーバから push されないため定期ポーリングで追従し、
 * チャットの切り替え (既読になる) と自分のターン境界では即座に取り直す。
 * 非表示タブやチャット以外の画面では丸が見えないのでポーリングを止め、表示に戻った時点で取り直す。
 */
export function useChatActivitySync(): void {
	const status = useChatStore((s) => s.status);
	const chatId = useChatStore((s) => s.chatId);
	const userId = useCurrentUserStore((s) => s.id);
	const chatsShown = useLayoutStore((s) => s.mainView === "chats");

	// 追い越された応答を捨てるための世代番号。イベント契機と定期実行の 2 系統が同じ状態を書くので、
	// 古いスナップショットで新しい状態を上書きしないようにする。
	const requestSeqRef = useRef(0);
	const appliedSeqRef = useRef(0);
	// 失敗の通知は 1 回の障害につき 1 度だけ。成功したら次の障害のために戻す。
	const errorShownRef = useRef(false);

	const sync = useCallback(async (targetUserId: string, activeChatId: string | null) => {
		const seq = ++requestSeqRef.current;
		try {
			const entries = await listChatActivity();
			if (seq <= appliedSeqRef.current) return;
			appliedSeqRef.current = seq;
			errorShownRef.current = false;

			const stored = readChatLastSeen(targetUserId);
			const { indicators, lastSeen } = resolveChatIndicators(entries, stored, activeChatId);
			// 空のマップは保存しない。保存すると「既読情報なし」の判定が潰れ、後から見えるように
			// なったチャット (プロジェクトへの追加直後など) が一斉に未読の丸で埋まる。
			const changed = stored === null || !sameChatLastSeen(stored, lastSeen);
			if (changed && Object.keys(lastSeen).length > 0) writeChatLastSeen(targetUserId, lastSeen);
			useChatStore.getState().setChatIndicators(indicators);
			useChatStore
				.getState()
				.setChatLastActivityAt(
					Object.fromEntries(entries.map((e) => [e.chatId, e.lastActivityAt])),
				);
		} catch (err: unknown) {
			console.error("チャットの実行状態の取得に失敗しました", err);
			// 追い越された古い要求の失敗で、表示中の新しい状態に誤ったトーストを被せない。
			if (seq <= appliedSeqRef.current) return;
			if (!errorShownRef.current) {
				errorShownRef.current = true;
				useToastStore
					.getState()
					.show({ type: "error", message: "チャットの実行状態を取得できませんでした。" });
			}
		}
	}, []);

	// マウント時・チャット画面への復帰・チャット切り替え・ターン境界で取得する。開いているチャットは応答時ではなく
	// 要求時の値で決める (切り替え直後に前のチャットの応答が来ても取り違えない)。
	// 既読情報が無いうちはチャット以外の画面でも取得し、起動時点の全件既読の種をまいておく。
	useEffect(() => {
		if (userId === "" || (!chatsShown && readChatLastSeen(userId) !== null)) return;
		void sync(userId, chatId);
	}, [status, chatId, userId, chatsShown, sync]);

	useEffect(() => {
		if (userId === "" || !chatsShown) return;
		const poll = () => {
			if (document.hidden) return;
			void sync(userId, useChatStore.getState().chatId);
		};
		const timer = setInterval(poll, POLL_INTERVAL_MS);
		document.addEventListener("visibilitychange", poll);
		return () => {
			clearInterval(timer);
			document.removeEventListener("visibilitychange", poll);
		};
	}, [userId, chatsShown, sync]);
}
